import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Typography } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import _ from 'lodash';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import { AUTO_COMPLETE_DEFAULT, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpTextField from '../../form/NgpTextField';
import NgpCheckbox from '../../form/NgpCheckbox';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';

import { FIELD_PRIORITY_RULES } from './PollingConstants';

const uniqueErrorText = i18n._(t`Value must be unique`);
const saveButtonText = i18n._(t`Save`);
const cancelText = i18n._(t`Cancel`);
const formId = 'priorityInterfacePatternRule';
const MAX_LENGTH = 50;

const defaultFormValues = {
    [FIELD_PRIORITY_RULES.PATTERN]: '',
    [FIELD_PRIORITY_RULES.MATCH_NAME]: true,
    [FIELD_PRIORITY_RULES.MATCH_DESCRIPTION]: true,
    [FIELD_PRIORITY_RULES.MATCH_TYPE]: true,
};

export default function PriorityInterfacePatternRule({
    selectedList = [],
    selectedRecord,
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
}) {
    // Override shouldUnregister: the MUI Dialog unmounts children when closed,
    // which would unregister the Controller-backed field and race with the
    // open-time trigger() call below.
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: defaultFormValues,
        mode: 'onChange',
        shouldUnregister: false,
    });
    const internalId = useRef(null);
    const { handleSubmit, formState, reset, trigger } = methods;
    // react-hook-form v6 does not expose `errors` through the formState
    // proxy, so a bump after trigger() forces a re-render so children read
    // the freshly-set errors.
    const [, bumpAfterTrigger] = useState(0);

    // Keep the latest list in a ref so the validate closure (registered once
    // with the field) always sees fresh data without being re-created.
    const selectedListRef = useRef(selectedList);
    selectedListRef.current = selectedList;

    const validateUnique = useCallback((value) => {
        const duplicate = _.find(
            selectedListRef.current,
            (item) =>
                _.toLower(item[FIELD_PRIORITY_RULES.PATTERN]) === _.toLower(value) &&
                item[INTERNAL_ID_FIELD] !== internalId.current
        );
        return duplicate ? uniqueErrorText : true;
    }, []);

    const fieldRules = useMemo(() => ({ validate: { unique: validateUnique } }), [validateUnique]);

    useEffect(() => {
        if (!open) return;

        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];
            reset(selectedRecord);
        }

        // Surface the required-field validation as soon as the dialog opens.
        // The setTimeout pushes the bump out of any batch RHF is in the
        // middle of, so the re-render reliably picks up the new errors.
        const id = window.setTimeout(() => {
            trigger(FIELD_PRIORITY_RULES.PATTERN).then(() => bumpAfterTrigger((c) => c + 1));
        }, 0);
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleCleanup = () => {
        internalId.current = null;
        setSelectedRecord(undefined);
        reset(defaultFormValues);
    };

    const handleFormSubmit = async (dataToSave = {}) => {
        if (internalId.current) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        // Uniqueness is enforced by the on-change `validate.unique` rule, so
        // the submit button is already disabled when there is a duplicate.
        await onConfirm(dataToSave);
        handleCleanup();
    };

    return (
        <NgpTwoButtonDialog
            title={<Trans>Priority Interface Pattern Rule</Trans>}
            confirmButtonText={saveButtonText}
            cancelButtonText={cancelText}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={formId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <NgpTextField
                        fieldName={FIELD_PRIORITY_RULES.PATTERN}
                        label={<Trans>Selected Field Contains</Trans>}
                        required
                        showCharCounter
                        fullWidth
                        fieldRules={fieldRules}
                        inputProps={{
                            maxLength: MAX_LENGTH,
                            autoComplete: AUTO_COMPLETE_DEFAULT,
                        }}
                    />
                    <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                        <Trans>Fields to Match</Trans>
                    </Typography>
                    <NgpCheckbox fieldName={FIELD_PRIORITY_RULES.MATCH_NAME} label={<Trans>Name</Trans>} />
                    <NgpCheckbox
                        fieldName={FIELD_PRIORITY_RULES.MATCH_DESCRIPTION}
                        label={<Trans>Description</Trans>}
                    />
                    <NgpCheckbox fieldName={FIELD_PRIORITY_RULES.MATCH_TYPE} label={<Trans>Interface Type</Trans>} />
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
